import { useState, useMemo } from "react";
import {
  FaUser,
  FaEnvelope,
  FaPhoneAlt,
  FaPaperPlane,
  FaMapMarkerAlt,
  FaClock,
  FaWhatsapp,
} from "react-icons/fa";
import { useSiteSettings } from "../contexts/SiteSettingsContext";

const WEB3FORM_ACCESS_KEY = process.env.REACT_APP_WEB3FORM_ACCESS_KEY;

const Contact = () => {
  const { settings } = useSiteSettings();

  const whatsappLink = useMemo(() => {
    const number = settings.whatsapp?.replace(/[^0-9]/g, "") || "919049830590";
    return `https://wa.me/${number}`;
  }, [settings.whatsapp]);

  const phoneLink = useMemo(() => {
    const number = settings.phone?.replace(/[^0-9+]/g, "") || "+919049830590";
    return `tel:${number}`;
  }, [settings.phone]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!WEB3FORM_ACCESS_KEY) {
      console.error("WEB3FORM_ACCESS_KEY is not configured");
      alert("Form temporarily unavailable. Please try again later.");
      return;
    }

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: WEB3FORM_ACCESS_KEY,
          name: formData.name,
          email: formData.email,
          phone: formData.phone || "Not provided",
          message: formData.message,
          subject: "New Contact Form Submission",
          from_name: "The Lingua Alliance - Contact Form",
        }),
      });

      const result = await response.json();

      if (result.success) {
        console.log("Contact form submitted successfully:", result);
        alert("Thank you for your message! We'll get back to you soon.");

        // Reset form
        setFormData({
          name: "",
          email: "",
          phone: "",
          message: "",
        });
      } else {
        console.error("Form submission failed:", result);
        alert("Failed to submit form. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("An error occurred. Please try again.");
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-canvas pb-20 text-ink overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-primary/10 py-8 sm:py-10">
        <div
          className="absolute inset-0 bg-cover bg-[center_20%]"
          style={{
            backgroundImage: `url('${process.env.PUBLIC_URL}/Contact.png')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/65 to-black/55" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-primary/10" />

        <div className="relative mx-auto flex max-w-6xl flex-col items-center gap-2 sm:gap-4 lg:gap-6 px-3 sm:px-6 py-8 sm:py-14 lg:py-20 text-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/30 to-primary-soft/30 rounded-full blur-2xl animate-pulse" />
            <h1 className="relative text-xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-white pb-2 drop-shadow-lg">
              Get in Touch
            </h1>
          </div>
          <p className="max-w-3xl text-[11px] sm:text-sm md:text-base lg:text-lg leading-relaxed text-white/90 drop-shadow-md px-2 sm:px-4">
            We are excited to connect with you. Share your goals and our
            advisors will design the right language journey.
          </p>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-6 sm:py-12 lg:py-16 bg-canvas">
        <div className="mx-auto max-w-6xl px-3 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-3 sm:gap-6 lg:gap-8 items-start">
            {/* Contact Form */}
            <div className="rounded-lg sm:rounded-2xl lg:rounded-3xl border border-primary/10 bg-gradient-to-br from-primary/5 via-white to-white/80 p-3 sm:p-6 lg:p-8 shadow-[0_28px_60px_rgba(15,23,42,0.18)]">
              <h2 className="text-base sm:text-xl lg:text-2xl font-semibold mb-2 sm:mb-3 lg:mb-4">
                Send us a Message
              </h2>
              <p className="text-[11px] sm:text-sm text-ink-muted mb-3 sm:mb-5 lg:mb-6">
                Fill out the form below and we'll get back to you within 24
                hours
              </p>

              <form
                onSubmit={handleSubmit}
                className="space-y-2.5 sm:space-y-4 lg:space-y-6"
              >
                <div>
                  <label
                    htmlFor="name"
                    className="block text-xs sm:text-sm font-medium text-ink mb-1.5 sm:mb-2"
                  >
                    Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative flex items-center rounded-lg border border-primary/20 bg-white focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
                    <FaUser className="ml-2.5 sm:ml-4 text-primary text-xs sm:text-base" />
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full rounded-lg border-0 bg-transparent px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm text-ink placeholder:text-ink-muted/60 focus:outline-none"
                      placeholder="Your full name"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-ink mb-2"
                  >
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative flex items-center rounded-lg border border-primary/20 bg-white focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
                    <FaEnvelope className="ml-2.5 sm:ml-4 text-primary text-xs sm:text-base" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full rounded-lg border-0 bg-transparent px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm text-ink placeholder:text-ink-muted/60 focus:outline-none"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-ink mb-2"
                  >
                    Phone
                  </label>
                  <div className="relative flex items-center rounded-lg border border-primary/20 bg-white focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
                    <FaPhoneAlt className="ml-2.5 sm:ml-4 text-primary text-xs sm:text-base" />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full rounded-lg border-0 bg-transparent px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm text-ink placeholder:text-ink-muted/60 focus:outline-none"
                      placeholder="+91 90000 00000"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-ink mb-2"
                  >
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-primary/20 bg-white px-2.5 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-ink placeholder:text-ink-muted/60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    placeholder="Tell us about your language learning goals, questions, or how we can help you..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full rounded-full bg-gradient-to-r from-primary to-primary-soft px-4 sm:px-8 py-2 sm:py-3 text-[11px] sm:text-sm font-semibold uppercase tracking-wide text-white shadow-[0_18px_30px_rgba(79,64,198,0.35)] transition hover:-translate-y-1 hover:shadow-[0_24px_40px_rgba(18,10,56,0.35)] flex items-center justify-center gap-1.5 sm:gap-2"
                >
                  <FaPaperPlane className="text-xs sm:text-sm" />
                  Send Message
                </button>
              </form>
            </div>

            {/* Contact Info & Map */}
            <div className="flex flex-col gap-3 sm:gap-6 h-full">
              {/* Institute Address with Map */}
              <div className="rounded-lg sm:rounded-2xl lg:rounded-3xl border border-primary/10 bg-white p-3 sm:p-6 shadow-elevated flex-1 flex flex-col">
                <div className="flex items-start gap-2 sm:gap-4 mb-3 sm:mb-6">
                  <div className="inline-flex h-9 w-9 sm:h-12 sm:w-12 items-center justify-center rounded-lg sm:rounded-xl bg-gradient-to-br from-primary/10 to-primary-soft/10 text-base sm:text-xl text-primary flex-shrink-0">
                    <FaMapMarkerAlt />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm sm:text-lg font-semibold text-ink mb-1.5 sm:mb-2">
                      Institute Address
                    </h3>
                    <p className="text-[11px] sm:text-sm text-ink-muted leading-relaxed">
                      {settings.address}
                    </p>
                  </div>
                </div>
                {/* Google Map Embed - Pune Location */}
                <div className="rounded-lg sm:rounded-xl overflow-hidden border border-primary/10 aspect-video flex-1 min-h-[180px] sm:min-h-[250px] lg:min-h-[300px]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d473.06509760981754!2d73.82201944125332!3d18.460055169478142!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc29540e5bbd0e1%3A0x3b633d7a6aebde1c!2sNavale%20Icon%2C%20Wadgaon%20Budruk%2C%20Narhe%2C%20Pune%2C%20Maharashtra%20411041%2C%20India!5e0!3m2!1sen!2sus!4v1772270524680!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full h-full"
                    title="The Lingua Alliance - Navale Icon, Pune"
                  ></iframe>
                </div>
              </div>

              {/* Working Hours */}
              <div className="rounded-lg sm:rounded-3xl border border-primary/10 bg-white p-3 sm:p-8 shadow-elevated">
                <div className="flex items-start gap-2 sm:gap-4">
                  <div className="inline-flex h-9 w-9 sm:h-12 sm:w-12 items-center justify-center rounded-lg sm:rounded-xl bg-gradient-to-br from-primary/10 to-primary-soft/10 text-base sm:text-xl text-primary flex-shrink-0">
                    <FaClock />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm sm:text-lg font-semibold text-ink mb-2 sm:mb-4">
                      Working Hours
                    </h3>
                    <div className="space-y-1.5 sm:space-y-3 text-[11px] sm:text-sm text-ink-muted">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-ink">
                          Monday - Friday
                        </span>
                        <span>09:00 - 19:00 IST</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-ink">Saturday</span>
                        <span>10:00 - 16:00 IST</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-ink">Sunday</span>
                        <span className="text-ink-muted">Closed</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Contact Section - Full Width */}
          <div className="mt-3 sm:mt-6 lg:mt-8 rounded-lg sm:rounded-2xl lg:rounded-3xl border border-primary/10 bg-gradient-to-br from-primary to-primary-soft p-3 sm:p-6 lg:p-8 shadow-elevated text-white">
            <h3 className="text-base sm:text-xl lg:text-2xl font-semibold mb-3 sm:mb-6 lg:mb-8 text-center">
              Quick Contact
            </h3>
            <div className="flex flex-col lg:flex-row items-center justify-center gap-3 sm:gap-6 lg:gap-10">
              <div className="flex flex-col gap-2 sm:gap-4 lg:gap-6 w-full lg:w-auto">
                <div className="flex items-center gap-1.5 sm:gap-3 lg:gap-4">
                  <div className="inline-flex h-8 w-8 sm:h-12 sm:w-12 lg:h-14 lg:w-14 items-center justify-center rounded-lg sm:rounded-xl bg-white/20 text-sm sm:text-xl lg:text-2xl flex-shrink-0">
                    <FaEnvelope />
                  </div>
                  <div className="text-left">
                    <p className="text-[10px] sm:text-sm text-white/80 mb-0.5 sm:mb-1">
                      Email
                    </p>
                    <p className="text-[11px] sm:text-sm lg:text-base font-medium break-all">
                      {settings.email}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-3 lg:gap-4">
                  <div className="inline-flex h-8 w-8 sm:h-12 sm:w-12 lg:h-14 lg:w-14 items-center justify-center rounded-lg sm:rounded-xl bg-white/20 text-sm sm:text-xl lg:text-2xl flex-shrink-0">
                    <FaPhoneAlt />
                  </div>
                  <div className="text-left">
                    <p className="text-[10px] sm:text-sm text-white/80 mb-0.5 sm:mb-1">
                      Phone
                    </p>
                    <p className="text-[11px] sm:text-sm lg:text-base font-medium">
                      {settings.phone}
                    </p>
                  </div>
                </div>
              </div>

              {/* WhatsApp & Call Buttons */}
              <div className="flex flex-col sm:flex-row gap-1.5 sm:gap-3 lg:gap-4 justify-center w-full lg:w-auto">
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1 sm:gap-2 rounded-full bg-white text-primary px-3 sm:px-6 lg:px-8 py-1.5 sm:py-2.5 lg:py-3 text-[11px] sm:text-sm font-semibold transition hover:bg-white/90 shadow-lg"
                >
                  <FaWhatsapp className="text-xs sm:text-base lg:text-lg" />
                  Chat on WhatsApp
                </a>
                <a
                  href={phoneLink}
                  className="flex items-center justify-center gap-1 sm:gap-2 rounded-full bg-white/10 border-2 border-white text-white px-3 sm:px-6 lg:px-8 py-1.5 sm:py-2.5 lg:py-3 text-[11px] sm:text-sm font-semibold transition hover:bg-white/20"
                >
                  <FaPhoneAlt className="text-[10px] sm:text-sm" />
                  Call Us Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
